import { useEffect } from "react";
import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { useDispatch, useSelector } from "react-redux";
import { call, debounce, put, select, takeEvery } from "redux-saga/effects";

import { FileToUpload } from "api/media-library";
import { CollectionFile, MediaFilters, MediaSort } from "shared/types/files";
import { SELECT_COLLECTION_ROUTINE } from "constants/ActionTypes";
import {
  selectVisibleMediaCollections,
  selectMediaCollectionsView,
} from "@/entities/media-collections/collections.model";
import { ReduxRootState } from "@/types/redux-store";

import {
  collectionMediaFilesActions,
  fetchCollectionMediaFiles,
  fetchCollectionTags,
  selectCollectionMediaFilesByIds,
  uploadFilesToCollection,
} from "../../collection-media-files.model";
import {
  selectedMediaActions,
  selectSelectedMediaIds,
  selectSelectedMediaSet,
} from "../../select-media.model";
import {
  computeUploadingProgress,
  computeUploadingProgressValue,
  selectUploadingFilesByIds,
  UploadingMode,
} from "../../uploading-files.model";
import { resetCollectionsTags } from "../collection-media/reset-collection-tags";

const featureKey = "mediaPicker" as const;
const filtersStorageKey = "libraryPickerFilter";

export const defaultFilters: MediaFilters = {
  sort: MediaSort.addedNewestToOldest,
  name: "",
  description: "",
  usage: null,
  tags: [],
  excludeAccounts: [],
  sourceName: undefined,
  favorite: false,
  mediaType: null,
};

export type MediaPickerState = {
  isOpen: boolean;
  filters: MediaFilters;
  currentPage: number;
  pageSize: number;
  pagesCount: number;
  collectionsTags: string[];
  showingFileIds: string[];
  uploadingFileIds: string[];
  lastSelectedCollectionId: string | null;
};

function readPersistedFilters(): MediaFilters {
  if (typeof window === "undefined") {
    return defaultFilters;
  }

  try {
    const storedValue = window.localStorage.getItem(filtersStorageKey);

    if (!storedValue) {
      return defaultFilters;
    }

    return {
      ...defaultFilters,
      ...JSON.parse(storedValue),
    };
  } catch {
    return defaultFilters;
  }
}

function persistFilters(filters: MediaFilters) {
  if (typeof window === "undefined") {
    return;
  }

  try {
    window.localStorage.setItem(filtersStorageKey, JSON.stringify(filters));
  } catch {
    // ignore storage write failures
  }
}

const initialState: MediaPickerState = {
  isOpen: false,
  filters: readPersistedFilters(),
  currentPage: 1,
  pageSize: 24,
  pagesCount: 0,
  collectionsTags: [],
  showingFileIds: [],
  uploadingFileIds: [],
  lastSelectedCollectionId: null,
};

const mediaPickerSlice = createSlice({
  name: featureKey,
  initialState,
  reducers: {
    gateOpened(state) {
      state.isOpen = true;
    },
    gateClosed(state) {
      state.isOpen = false;
      state.uploadingFileIds = [];
    },
    filtersChanged(state, action: PayloadAction<MediaFilters>) {
      state.filters = action.payload;
      state.currentPage = 1;
    },
    filtersReset(state) {
      state.filters = defaultFilters;
      state.currentPage = 1;
    },
    setPage(state, action: PayloadAction<number>) {
      state.currentPage = action.payload;
    },
    pageSizeChanged(state, action: PayloadAction<number>) {
      state.pageSize = action.payload;
      state.currentPage = 1;
    },
    collectionFilesLoaded(
      state,
      action: PayloadAction<{
        ids: string[];
        pagesCount: number;
      }>
    ) {
      state.showingFileIds = action.payload.ids;
      state.pagesCount = action.payload.pagesCount;
    },
    collectionsTagsLoaded(state, action: PayloadAction<string[]>) {
      state.collectionsTags = action.payload;
    },
    collectionsTagsCleared(state) {
      state.collectionsTags = [];
    },
    showingFilesCleared(state) {
      state.showingFileIds = [];
      state.pagesCount = 0;
    },
    uploadedFileReceived(state, action: PayloadAction<string>) {
      state.showingFileIds = [
        action.payload,
        ...state.showingFileIds.filter((id) => id !== action.payload),
      ].slice(0, state.pageSize);
    },
    uploadingIdsSet(state, action: PayloadAction<string[]>) {
      state.uploadingFileIds = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(SELECT_COLLECTION_ROUTINE.REQUEST, (state, action) => {
      state.lastSelectedCollectionId =
        action.payload.id ?? state.lastSelectedCollectionId;
      state.collectionsTags = [];
      state.showingFileIds = [];
      state.pagesCount = 0;
    });
    builder.addCase(resetCollectionsTags, (state) => {
      state.collectionsTags = [];
    });
  },
});

export const mediaPickerReducer = mediaPickerSlice.reducer;
export const mediaPickerActions = mediaPickerSlice.actions;

export const collectionFileSelected = selectedMediaActions.add;
export const collectionFileDeselected = selectedMediaActions.remove;
export const collectionFilesClear = selectedMediaActions.clear;
export const filtersChanged = mediaPickerActions.filtersChanged;
export const filtersReset = mediaPickerActions.filtersReset;
export const setPage = mediaPickerActions.setPage;
export const pageSizeChanged = mediaPickerActions.pageSizeChanged;

function getMediaPickerState(state: ReduxRootState): MediaPickerState {
  return (
    (
      state as ReduxRootState & {
        [featureKey]?: MediaPickerState;
      }
    )[featureKey] ?? initialState
  );
}

export const selectMediaPickerIsOpen = createSelector(
  getMediaPickerState,
  (state) => state.isOpen
);
export const selectMediaPickerFilters = createSelector(
  getMediaPickerState,
  (state) => state.filters
);
export const selectMediaPickerCurrentPage = createSelector(
  getMediaPickerState,
  (state) => state.currentPage
);
export const selectMediaPickerPageSize = createSelector(
  getMediaPickerState,
  (state) => state.pageSize
);
export const selectMediaPickerPagesCount = createSelector(
  getMediaPickerState,
  (state) => state.pagesCount
);
export const selectMediaPickerCollectionsTags = createSelector(
  getMediaPickerState,
  (state) => state.collectionsTags
);
export const selectMediaPickerShowingFileIds = createSelector(
  getMediaPickerState,
  (state) => state.showingFileIds
);
export const selectMediaPickerUploadingFileIds = createSelector(
  getMediaPickerState,
  (state) => state.uploadingFileIds
);

const selectCollectionsState = (state: ReduxRootState) =>
  state.entities?.collections ?? {
    byId: {},
    selectedIds: [],
  };

export const selectCurrentMediaPickerCollection = createSelector(
  getMediaPickerState,
  selectCollectionsState,
  (mediaPickerState, collectionsState) => {
    const selectedId =
      collectionsState.selectedIds?.[0] ??
      mediaPickerState.lastSelectedCollectionId;

    if (!selectedId) {
      return null;
    }

    return collectionsState.byId?.[selectedId] ?? null;
  }
);

export const selectMediaPickerModel = createSelector(
  (state: ReduxRootState) => state,
  selectVisibleMediaCollections,
  selectCurrentMediaPickerCollection,
  selectMediaPickerFilters,
  selectMediaPickerCollectionsTags,
  selectMediaPickerCurrentPage,
  selectMediaPickerPageSize,
  selectMediaPickerPagesCount,
  selectSelectedMediaSet,
  selectSelectedMediaIds,
  selectMediaPickerShowingFileIds,
  selectMediaPickerUploadingFileIds,
  selectMediaCollectionsView,
  (
    state,
    collections,
    currentCollection,
    filters,
    collectionsTags,
    currentPage,
    pageSize,
    pagesCount,
    selectedMediaSet,
    selectedMediaIds,
    showingFileIds,
    uploadingFileIds,
    collectionsView
  ) => {
    const selectedMediaFiles = selectCollectionMediaFilesByIds(
      state,
      selectedMediaIds
    );
    const media = selectCollectionMediaFilesByIds(state, showingFileIds);
    const uploadingFiles = selectUploadingFilesByIds(state, uploadingFileIds);

    return {
      collections,
      currentCollection,
      filters,
      collectionsTags,
      currentPage,
      pageSize,
      pagesCount,
      totalItems: pagesCount * pageSize,
      selectedMediaSet,
      selectedMediaFiles,
      media,
      uploadingStatus: computeUploadingProgress(uploadingFiles),
      uploadingProgressValue: computeUploadingProgressValue(uploadingFiles),
      pendingCollections: collectionsView.isLoading,
    };
  }
);

export function useMediaPickerModel() {
  return useSelector(selectMediaPickerModel);
}

export function useMediaPickerGate() {
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(mediaPickerActions.gateOpened());

    return () => {
      dispatch(mediaPickerActions.gateClosed());
      dispatch(selectedMediaActions.clear());
    };
  }, [dispatch]);
}

export function userFilesSelected(
  files: FileToUpload[],
  options?: {
    onFileUploaded?: (file: CollectionFile) => void;
  }
) {
  return async (
    dispatch: (action: unknown) => unknown,
    getState: () => ReduxRootState
  ) => {
    const state = getState();
    const currentCollection = selectCurrentMediaPickerCollection(state);

    if (!currentCollection?._id) {
      return [];
    }

    const currentSize = selectSelectedMediaIds(state).length;
    let uploadedCount = 0;

    const uploadIds = (await dispatch(
      uploadFilesToCollection({
        files,
        collectionId: currentCollection._id,
        uploadingMode: "subsequent" as UploadingMode,
        onFileUploaded(file: CollectionFile) {
          dispatch(
            selectedMediaActions.addAtIndex({
              id: file._id,
              index: currentSize + uploadedCount,
            })
          );
          uploadedCount += 1;
          dispatch(mediaPickerActions.uploadedFileReceived(file._id));
          options?.onFileUploaded?.(file);
        },
      }) as never
    )) as string[];

    dispatch(mediaPickerActions.uploadingIdsSet(uploadIds));

    return uploadIds;
  };
}

function* persistFiltersSaga() {
  const filters: MediaFilters = yield select(selectMediaPickerFilters);
  yield call(persistFilters, filters);
}

function* fetchCurrentCollectionFilesSaga() {
  const state: ReduxRootState = yield select();
  const isOpen = selectMediaPickerIsOpen(state);
  const currentCollection = selectCurrentMediaPickerCollection(state);

  if (!isOpen || !currentCollection?._id) {
    return;
  }

  const filters = selectMediaPickerFilters(state);
  const currentPage = selectMediaPickerCurrentPage(state);
  const pageSize = selectMediaPickerPageSize(state);
  try {
    const response: { list: CollectionFile[]; pagesCount: number } = yield call(
      fetchCollectionMediaFiles,
      {
        collectionId: currentCollection._id,
        page: currentPage,
        limit: pageSize,
        ...filters,
      }
    );

    yield put(collectionMediaFilesActions.setFiles(response.list));
    yield put(
      mediaPickerActions.collectionFilesLoaded({
        ids: response.list.map((item) => item._id),
        pagesCount: response.pagesCount,
      })
    );
  } catch {
    // Preserve the watcher so later pagination and filter changes still refetch.
  }
}

function* reloadCollectionTagsSaga() {
  const state: ReduxRootState = yield select();
  const currentCollection = selectCurrentMediaPickerCollection(state);

  yield put(mediaPickerActions.collectionsTagsCleared());

  if (!currentCollection?._id) {
    return;
  }

  try {
    const tags: string[] = yield call(
      fetchCollectionTags,
      currentCollection._id
    );
    yield put(mediaPickerActions.collectionsTagsLoaded(tags));
  } catch {
    // Preserve the watcher so future collection changes can recover.
  }
}

export function* watchMediaPickerModel() {
  yield debounce(
    300,
    [
      mediaPickerActions.gateOpened.type,
      mediaPickerActions.filtersChanged.type,
      mediaPickerActions.filtersReset.type,
      mediaPickerActions.setPage.type,
      mediaPickerActions.pageSizeChanged.type,
      SELECT_COLLECTION_ROUTINE.REQUEST,
    ],
    fetchCurrentCollectionFilesSaga
  );
  yield takeEvery(
    [
      mediaPickerActions.gateOpened.type,
      SELECT_COLLECTION_ROUTINE.REQUEST,
      resetCollectionsTags.type,
    ],
    reloadCollectionTagsSaga
  );
  yield takeEvery(
    [
      mediaPickerActions.filtersChanged.type,
      mediaPickerActions.filtersReset.type,
    ],
    persistFiltersSaga
  );
}
